/**
 * SOLID Review
 * S — Strong. One concern: what the table's phase decides — whose move the table waits on, what the cup does,
 *     whether there is a round to catch up on, and what a round's start and a roster update do in it.
 * O — Strong. A new phase is one subclass and one line in `of`; nothing outside switches on the phase. A new
 *     phase-dependent rule is one question here, answered by the default and overridden where it differs.
 * L — Strong. Every caller holds a `TablePhase` and cannot tell which; no subclass strengthens a precondition or
 *     weakens an answer.
 * I — Good. Every question is one the table asks of whichever phase it is in; most phases answer the defaults.
 * D — Strong. The Domain's phases, the table's seats and lives, and the schema types.
 */
import { GamePhase } from '../uml-components/primitives';
import { Roster, type IEntityRosterUpdatePlayer } from './Roster';
import { TableLives } from './TableLives';
import type { IStateClientTable, IEntityClientRoom, IEntityClientTableDerived } from '../ports/schemas/ClientTable';
import type { IPayloadRoundStarted } from '../ports/schemas/TableEvents';

/** What a tap on the cup does: roll, peek, or nothing. */
export type TCupAction = IEntityClientTableDerived['cupAction'];

/**
 * THE TABLE'S PHASE, AS A STATE OBJECT (frontend-domain-oo P3, 2026-10-26; handbook §28.270; the owner's decision of
 * 2026-10-11). The phase was a 10-value enum tested in six files: the cup's dispatch (`cupMove`, `cupActionOf`),
 * the awaited player (`game/BlockingSuspension.ts`), the live play a resync catches up (`LIVE_PLAY`), the reveal on
 * screen (`SHOWING_REVEAL`), the lobby's reads and its roster, a round's start in the lobby and after a game, and
 * the move into idle that forgets the stored game. Each is a question here, asked of whichever phase the table is in.
 *
 * `GamePhase` stays the snapshot's discriminator, and `of` maps it to its class; the subclasses are not exported, so
 * no caller can hold one and rediscover the switch it replaced (backend `RoundAdvance`, domain-oo). Two phases
 * group the rest: IN PLAY (a round to catch up on) and THE ROLLER'S MOVE (the table waits on the roller).
 *
 * Only what depended on the phase moved here — the rest of the table's rules are `ClientTable`'s, the same in
 * every phase. A move the handlers never refused by phase is not refused by one now (a claim, a believe, a lift):
 * the server judges those, and the golden corpus holds the table to the handlers' answers.
 */
export abstract class TablePhase {
  /** The State for a snapshot's phase. */
  static of(phase: GamePhase): TablePhase {
    switch (phase) {
      case GamePhase.IDLE: return new Idle();
      case GamePhase.LOBBY: return new Lobby();
      case GamePhase.STARTING: return new Starting();
      case GamePhase.ROLLING: return new Rolling();
      case GamePhase.PEEKING: return new Peeking();
      case GamePhase.CLAIMING: return new Claiming();
      case GamePhase.DECIDING: return new Deciding();
      case GamePhase.REVEALING: return new Revealing();
      case GamePhase.ROUND_END: return new RoundEnd();
      case GamePhase.GAME_OVER: return new GameOver();
    }
  }

  /** What a tap on the cup does for the roller with a round: nothing, unless the phase is the cup's. */
  cupAction(_diceRevealed: boolean): TCupAction {
    return null;
  }

  /**
   * The player whose action the phase waits on (`resolveAwaitedPlayerId`): nobody, unless the phase awaits a
   * specific player's input — not a reveal, the lobby, idle or a round's transition.
   */
  awaitedPlayer(_rollerId: string | null, _challengerId: string | null): string | null {
    return null;
  }

  /** A round in play, which a reconnect may have missed moves of: not the lobby, idle or game over. */
  hasARoundToCatchUp(): boolean {
    return false;
  }

  /** The lobby: the table has not started, and the lobby's reads still apply. */
  isLobby(): boolean {
    return false;
  }

  /** A reveal is on screen: a newer one the client missed is not shown over it. */
  showsTheReveal(): boolean {
    return false;
  }

  /** Not at a table: the store's initial phase, and where leaving or signing out returns. */
  isIdle(): boolean {
    return false;
  }

  /**
   * A round started, in a game (ControlPanel's handler): every trace of the last round is cleared, including the
   * server's decider (the new round's is computed afresh) and the decision countdown (the server never cancels
   * one, so "Auto-believe in 0s" stuck around); the table rolls.
   */
  roundStarted(table: IStateClientTable, round: IPayloadRoundStarted, _room: IEntityClientRoom): void {
    table.activeRoundId = round.roundId ?? null;
    table.activeRollerId = round.rollerId ?? null;
    table.minimumClaimScore = round.minimumScore ?? 0;
    table.lastClaimedScore = null;
    table.claimWasBlind = false;
    table.diceRevealed = false;
    table.lastDiceRoll = null;
    table.opponentClaim = null;
    table.revealResult = null;
    table.activeChallengerId = null;
    table.decisionCountdown = null;
    table.phase = GamePhase.ROLLING;
  }

  /**
   * Someone joined or left, in a game: the roster is MERGED, so a late join never resets the lives and seats of
   * the players already there.
   */
  rosterAfter(seats: Roster, players: ReadonlyArray<IEntityRosterUpdatePlayer> | null | undefined, localPlayerId: string | null): Roster {
    return seats.merged(players, localPlayerId);
  }
}

/** A round is in play: a reconnect catches it up. */
abstract class InPlay extends TablePhase {
  hasARoundToCatchUp(): boolean {
    return true;
  }
}

/** The roller rolls, peeks or claims: the table waits on them. */
abstract class RollersMove extends InPlay {
  awaitedPlayer(rollerId: string | null, _challengerId: string | null): string | null {
    return rollerId;
  }
}

/** No table: no identity or no room yet, or the table was left. */
class Idle extends TablePhase {
  isIdle(): boolean {
    return true;
  }
}

/**
 * Seated, the game not started. Two handlers were chosen by phase as their components' mounting chose them:
 * LobbyPanel's sync hooks ran only here, ControlPanel's only in a game.
 */
class Lobby extends TablePhase {
  isLobby(): boolean {
    return true;
  }

  /**
   * The game's first round (LobbyPanel's `useLobbyRoundStartedSync`): roller and round when named, every seat at
   * full lives, and the table rolls.
   */
  roundStarted(table: IStateClientTable, round: IPayloadRoundStarted, room: IEntityClientRoom): void {
    if (round.rollerId) table.activeRollerId = round.rollerId;
    if (round.roundId) table.activeRoundId = round.roundId;
    if (room.players.length > 0) table.playerLives = TableLives.full(Roster.of(room.players).ids()).toRecord();
    table.phase = GamePhase.ROLLING;
  }

  /**
   * In the lobby every seat is rebuilt from the server's list — LobbyPanel's sync ran AFTER or BEFORE ControlPanel's
   * merge, and either way the rebuild was what stood.
   */
  rosterAfter(_seats: Roster, players: ReadonlyArray<IEntityRosterUpdatePlayer> | null | undefined, localPlayerId: string | null): Roster {
    return Roster.fresh(players, localPlayerId);
  }
}

/** A phase no decision enters; a snapshot may still carry it. It awaits nobody and offers no move. */
class Starting extends TablePhase {}

/** The roller's first tap rolls; the server keeps the dice secret until they peek. */
class Rolling extends RollersMove {
  cupAction(_diceRevealed: boolean): TCupAction {
    return 'roll';
  }
}

/** The roller has rolled; their second tap peeks, once — with the dice seen the cup does nothing. */
class Peeking extends RollersMove {
  cupAction(diceRevealed: boolean): TCupAction {
    return diceRevealed ? null : 'peek';
  }
}

/** The roller chooses a claim (no decision enters it; the claim is announced from PEEKING). */
class Claiming extends RollersMove {}

/** A claim is on the table: the decider believes or lifts it, and the table waits on them. */
class Deciding extends InPlay {
  awaitedPlayer(_rollerId: string | null, challengerId: string | null): string | null {
    return challengerId;
  }
}

/** The cup is lifted and the reveal on screen; the loser rolls first. */
class Revealing extends InPlay {
  showsTheReveal(): boolean {
    return true;
  }
}

/** A phase no decision enters; a snapshot may still carry it. It awaits nobody and offers no move. */
class RoundEnd extends TablePhase {}

/** The first player out ended the game; the last reveal stays on screen until Play Again. */
class GameOver extends TablePhase {
  showsTheReveal(): boolean {
    return true;
  }

  /** A new game's first round (Play Again): every seat back to full lives, then as any round. */
  roundStarted(table: IStateClientTable, round: IPayloadRoundStarted, room: IEntityClientRoom): void {
    table.playerLives = TableLives.full(Roster.of(room.players).ids()).toRecord();
    super.roundStarted(table, round, room);
  }
}
